import Typography from "@mui/material/Typography";
import {Box, Button, Divider, FormControl, InputLabel, MenuItem, Select, Stack, Tab, Tabs} from "@mui/material";
import * as React from "react";
import {useState} from "react";
import {ConfirmationSettings, Sector, SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {useTranslation} from "react-i18next";
import {useQuery, useQueryClient} from "react-query";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {SectorService} from "crqlar-react-common/dist/services/sector/sector.service";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import ConfirmationSettingsSection from "./ConfirmationSettingsSection";
import ConnectCustomDomainDialog from "./ConnectCustomDomainDialog";
import {CustomDomainService} from "crqlar-react-common/dist/services/newsletter/customDomain.service";
import TransactionalMailTemplateList from "./TransactionalMailTemplateList";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import SettingsEntityHeader from "../layout/SettingsEntityHeader";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";

const TransactionalMailsSettings = () => {
    useTrackScreenVisit('system transactional mail settings')

    const {t} = useTranslation()
    const {sectorId} = useParams()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const queryClient = useQueryClient()

    const sectors = useAllSectors()
    // Custom Domain dropdown lists only this organization's sectors, excluding
    // accommodation/room reservation sectors.
    const dropdownSectors = sectors.filter(s => s.sectorType !== SectorType.ACCOMMODATION)
    const [customDomainDialogOpen, setCustomDomainDialogOpen] = useState(false)
    const requestedTab = searchParams.get('tab')
    const activeTab = requestedTab === 'templates' ? 'templates' : 'settings'
    const setActiveTab = (newTab: string) => {
        const nextSearchParams = new URLSearchParams(searchParams)
        nextSearchParams.set('tab', newTab)
        setSearchParams(nextSearchParams)
    }

    const readOnlyUser = useReadOnly()

    const customDomainQ = useQuery(
        ['customDomain'],
        () => CustomDomainService.get(),
        {
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
        }
    )

    const defaultSector = dropdownSectors?.[0]

    const passedSector = dropdownSectors.find(s => s.id === sectorId)

    const [chosenSector, setChosenSector] = useState<Sector | undefined>(passedSector || defaultSector)
    const chosenSectorId = chosenSector?.id

    React.useEffect(() => {
        if (passedSector && passedSector.id !== chosenSectorId) {
            setChosenSector(passedSector)
            return
        }
        if (!chosenSectorId && defaultSector) setChosenSector(defaultSector)
    }, [passedSector, chosenSectorId, defaultSector])

    const sectorData = useEntity({
        get: async () => {
            if (!sectors.length) return {}
            if (chosenSector?.id && chosenSector?.id !== 'new') {
                return SectorService.getSectorById(chosenSector?.id)
            } else {
                return {}
            }
        },
        create: SectorService.createSector,
        update: SectorService.updateSector,
        delete: (id) => SectorService.deleteSector({id: [id]}),
        onDeleted: () => navigate(-1),
        onSaved: (sector, wasCreateAction, prev) => {
            queryClient.invalidateQueries(['allSingleSector', sector.id])

            if (wasCreateAction) {
                navigate(`/system/sectors/${sector.id}`, {replace: true})
            }
        },
        isValid: (sector) => {
            return !!sector.name
        },
        cleanupBeforeSave: (sector) => {
            return {
                ...sector,
                name: sector.name?.trim()
            }
        },
    }, chosenSector)

    const sector = Object.keys(sectorData.item).length === 0 ? defaultSector : sectorData.item

    const setSector = sectorData.setItem

    const updateField = (field: keyof Sector, value: any) => {
        setSector({...sector, [field]: value})
    }

    const updateConfirmationSetting = (event: keyof ConfirmationSettings, value: any) => {
        updateField('notificationSettings', {
            ...sector?.notificationSettings,
            confirmationSettings: {
                ...sector?.notificationSettings?.confirmationSettings,
                [event]: value
            }
        })
    }

    const renderSectorDropdown = () => {
        if (!dropdownSectors.length) return null

        return (
            <FormControl variant="outlined" size={'small'} sx={{width: {xs: '100%', sm: 260}}}>
                <InputLabel id="sector-label">{t('Sector')}</InputLabel>
                <Select
                    labelId="sector-label"
                    label={t('Sector')}
                    value={chosenSector?.id || defaultSector?.id || ''}
                    onChange={(e) => {
                        const selectedId = e.target.value as string;
                        const selectedSector = dropdownSectors.find(s => s.id === selectedId) || null;
                        setChosenSector(selectedSector ?? undefined);
                    }}
                >
                    {dropdownSectors.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                            {s.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        )
    }

    const sectionTitleSx = {
        fontSize: {xs: '18px', sm: '20px'},
        lineHeight: 1.25,
    }

    const sectionDividerSx = {
        display: {xs: 'none', sm: 'block'},
    }

    const headerSection = (
        <SettingsEntityHeader
            title={t('Transactional Mails')}
            data={sectorData}
            back={() => navigate('/system')}
        />
    )

    const tabsSection = (
        <Tabs
            value={activeTab}
            onChange={(_, newTab) => setActiveTab(newTab)}
            aria-label="transactional mail settings tabs"
            sx={{
                mb: 4,
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            <Tab label={t('Email settings')} value={'settings'}/>
            <Tab label={t('Templates')} value={'templates'}/>
        </Tabs>
    )

    const setupSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Setup')}
            </Typography>

            <Stack gap={1.5} alignItems={'flex-start'}>
                <Typography variant={'body1'} textAlign={'start'} sx={{fontWeight: 500}}>
                    {t('Sending domain')}
                </Typography>

                <Button
                    disabled={readOnlyUser}
                    variant={'contained'}
                    onClick={() => setCustomDomainDialogOpen(true)}
                >
                    {customDomainQ.data ? t('Connected domain') : t('Connect custom sending domain')}
                </Button>
            </Stack>
        </Stack>
    )

    const sectorSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Sector')}
            </Typography>

            {renderSectorDropdown()}
        </Stack>
    )

    const emailSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('SendGrid Email Templates')}
            </Typography>

            <ConfirmationSettingsSection
                confirmationSettings={sector?.notificationSettings?.confirmationSettings}
                onSettingChange={updateConfirmationSetting}
                showSectionTitle={false}
                section={'sendgrid'}
            />
        </Stack>
    )

    const otherSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'} sx={sectionTitleSx}>
                {t('Other')}
            </Typography>

            <ConfirmationSettingsSection
                confirmationSettings={sector?.notificationSettings?.confirmationSettings}
                onSettingChange={updateConfirmationSetting}
                showSectionTitle={false}
                section={'other'}
            />
        </Stack>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    mt: 3,
                    gap: 2,
                }}
            >
                {tabsSection}
            </Stack>

            {activeTab === 'settings' && (
                <Stack
                    sx={{
                        width: '100%',
                        maxWidth: '900px',
                        mx: 'auto',
                        gap: {xs: 5, sm: 6},
                    }}
                >
                    {sectorSection}
                    <Divider sx={sectionDividerSx}/>
                    {emailSection}
                    <Divider sx={sectionDividerSx}/>
                    {otherSection}
                    <Divider sx={sectionDividerSx}/>
                    {setupSection}

                    <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                        {t('Last updated')}: {sector?.updateDate?.toLocaleString() || '-'}
                    </Typography>
                </Stack>
            )}

            {activeTab === 'templates' && (
                <Stack
                    sx={{
                        width: '100%',
                        maxWidth: '900px',
                        mx: 'auto',
                        gap: 6,
                    }}
                >
                    <TransactionalMailTemplateList/>
                </Stack>
            )}

            <ConnectCustomDomainDialog
                open={customDomainDialogOpen}
                onClose={() => setCustomDomainDialogOpen(false)}
                existingDomain={customDomainQ.data ?? null}
                onDomainSaved={() => customDomainQ.refetch()}
            />
        </Box>
    )
}

export default TransactionalMailsSettings
